import React from 'react';
import { barPercent, countAxisTicks } from '../../utils/statsPeriod';

/**
 * Hand-built charts for the Stats page (no chart library — see docs/design/family-stats.md).
 * Plain HTML/CSS bars: light, themeable for dark mode, readable on small screens.
 */

export interface ColumnSegment { value: number; color: string; title?: string }
export interface Column { key: string; label: string; segments: ColumnSegment[] }

/** Vertical columns, optionally stacked. Total shown above each column. */
export const ColumnChart: React.FC<{ columns: Column[]; height?: number; ariaLabel: string }> = ({
  columns, height = 140, ariaLabel,
}) => {
  const totals = columns.map((c) => c.segments.reduce((s, x) => s + x.value, 0));
  const max = Math.max(0, ...totals);
  return (
    <div role="img" aria-label={ariaLabel} className="w-full overflow-x-auto">
      <div className="flex items-end gap-1.5 min-w-full" style={{ height: height + 40 }}>
        {columns.map((c, i) => {
          const pct = barPercent(totals[i], max);
          return (
            <div key={c.key} className="flex-1 min-w-[22px] flex flex-col items-center justify-end h-full">
              <div className="text-[13px] font-bold text-kid-text dark:text-kid-text-dark mb-1 h-4">
                {totals[i] > 0 ? totals[i] : ''}
              </div>
              <div
                className="w-full max-w-[44px] rounded-t-lg overflow-hidden flex flex-col-reverse bg-kid-chip dark:bg-kid-chip-dark"
                style={{ height: (height * pct) / 100 || 2 }}
                title={`${c.label}: ${totals[i]}`}
              >
                {c.segments.filter((s) => s.value > 0).map((s, j) => (
                  <div key={j} title={s.title} style={{ height: `${(s.value / (totals[i] || 1)) * 100}%`, background: s.color }} />
                ))}
              </div>
              <div className="text-[13px] font-bold text-kid-text dark:text-kid-text-dark mt-1 whitespace-nowrap">{c.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export interface BarRow {
  key: string;
  label: string;
  value: number;
  color?: string;
  avatarSrc?: string;
  prefix?: string; // e.g. "1." in ranked view
  suffix?: string; // e.g. "pts"
}

/** Horizontal labelled bars (rooms, members, tasks). */
export const BarList: React.FC<{ rows: BarRow[]; ariaLabel: string }> = ({ rows, ariaLabel }) => {
  const max = Math.max(0, ...rows.map((r) => r.value));
  return (
    <ul aria-label={ariaLabel} className="space-y-2">
      {rows.map((r) => (
        <li key={r.key} className="flex items-center gap-2">
          {r.prefix && <span className="w-6 text-right text-sm font-bold text-kid-text-muted dark:text-kid-text-muted-dark">{r.prefix}</span>}
          {r.avatarSrc && <img src={r.avatarSrc} alt="" className="w-7 h-7 rounded-full object-cover border shrink-0" />}
          <span className="w-28 sm:w-40 shrink-0 truncate text-base font-bold">{r.label}</span>
          <div className="flex-1 h-5 rounded-full bg-kid-chip dark:bg-kid-chip-dark overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${barPercent(r.value, max)}%`, background: r.color || '#2E7D4F' }}
            />
          </div>
          <span className="w-14 text-right text-base font-bold">
            {r.value}{r.suffix ? ` ${r.suffix}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
};

export interface CountBar { key: string; label: string; value: number; color: string }

/**
 * Vertical bar chart with a whole-number y-axis (times completed) and one bar per family member,
 * names along the bottom. Used for each repeating task on the Stats "Tasks" tab.
 */
export const CountBarChart: React.FC<{ bars: CountBar[]; ariaLabel: string; height?: number }> = ({
  bars, ariaLabel, height = 120,
}) => {
  const ticks = countAxisTicks(Math.max(0, ...bars.map((b) => b.value)));
  const top = ticks[ticks.length - 1];
  return (
    <div role="img" aria-label={ariaLabel} className="flex gap-2">
      {/* y-axis labels */}
      <div className="relative w-6 shrink-0 text-[11px] text-kid-text-muted dark:text-kid-text-muted-dark" style={{ height }}>
        {ticks.map((t) => (
          <span key={t} className="absolute right-0 -translate-y-1/2 leading-none" style={{ top: height - (t / top) * height }}>
            {t}
          </span>
        ))}
      </div>
      <div className="flex-1 min-w-0">
        {/* plot area with gridlines */}
        <div className="relative border-l border-b border-kid-border-strong dark:border-kid-border-strong-dark" style={{ height }}>
          {ticks.slice(1).map((t) => (
            <div key={t} className="absolute left-0 right-0 border-t border-dashed border-kid-border dark:border-kid-border-dark"
              style={{ top: height - (t / top) * height }} />
          ))}
          <div className="absolute inset-0 flex items-end justify-around gap-2 px-2">
            {bars.map((b) => (
              <div key={b.key} className="flex-1 max-w-[56px] flex flex-col items-center justify-end h-full">
                {b.value > 0 && <span className="text-[11px] font-semibold text-kid-text dark:text-kid-text-dark mb-0.5">{b.value}</span>}
                <div className="w-full rounded-t-md" title={`${b.label}: ${b.value}`}
                  style={{ height: (b.value / top) * height, background: b.color }} />
              </div>
            ))}
          </div>
        </div>
        {/* x-axis: member names */}
        <div className="flex justify-around gap-2 px-2 mt-1">
          {bars.map((b) => (
            <span key={b.key} className="flex-1 max-w-[64px] text-center text-xs leading-tight text-kid-text-muted dark:text-kid-text-muted-dark break-words line-clamp-2" title={b.label}>
              {b.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
